import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { VegMark } from '../../components/MenuItemCard';
import {
  Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { sidesOf } from '../../utils/vertical';
import { currency } from '../../utils/format';

/**
 * KAAMWALA's master menu. The restaurant ticks the dishes it serves; each
 * lands on its own menu as an item it can then reprice and customise.
 */
/** The words for each master list: what an entry is, where it lands. */
const LIST = {
  dish: { title: 'Pick from the master menu', hint: 'Tick the dishes you serve, then set your own prices', one: 'dish', many: 'dishes', into: 'my menu', emoji: '🍽️', search: 'Search dishes' },
  product: { title: 'Pick from the master product list', hint: 'Tick what you stock, then set your own prices', one: 'product', many: 'products', into: 'my products', emoji: '📦', search: 'Search products, brands' },
  service: { title: 'Pick from the master service list', hint: 'Tick what you offer, then set your own prices', one: 'service', many: 'services', into: 'my services', emoji: '🛠️', search: 'Search services' },
};

const MasterMenuPage = () => {
  const { partner } = useAuth();
  /*
   * A greengrocer sells fruit; there is no such thing as a fruit service, and
   * offering the choice only invites a shop to build a list nobody can book.
   */
  const sides = sidesOf(partner);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [itemType, setItemType] = useState(params.get('type') || '');
  const [showAll, setShowAll] = useState(false);
  const words = LIST[itemType] || LIST.dish;
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [search, setSearch] = useState('');
  const [foodType, setFoodType] = useState('');
  const [chosen, setChosen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.menu.templates({ search: search || undefined, foodType: foodType || undefined, itemType: itemType || undefined, all: showAll ? '1' : undefined })
      .then((r) => {
        setItems(r.data.items || []);
        setSections(r.data.sections || []);
        // The server picks the list for this business when none was asked for.
        if (!itemType && r.data.itemType) setItemType(r.data.itemType);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, foodType, itemType, showAll]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const toggle = (id) => setChosen((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const adopt = async () => {
    setAdding(true);
    try {
      const res = await api.menu.adopt({ ids: chosen });
      toast.success(res.message, 'Added to your menu');
      navigate('/partner/catalogue?tab=products');
    } catch (err) {
      toast.error(err.message, 'Could not add');
    } finally { setAdding(false); }
  };

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader title={words.title} subtitle={words.hint} back />

      <div className="card sh-sm" style={{ marginBottom: 14 }}>
        <Input icon="🔍" placeholder={words.search} value={search} onChange={(e) => setSearch(e.target.value)} style={{ marginBottom: 10 }} />
        <div className="row wrap" style={{ gap: 8 }}>
          {itemType === 'dish' ? (
            <>
              <Chip label="All" active={!foodType} onClick={() => setFoodType('')} />
              <Chip label="🟢 Veg" active={foodType === 'veg'} onClick={() => setFoodType('veg')} />
              <Chip label="🔴 Non-veg" active={foodType === 'non_veg'} onClick={() => setFoodType('non_veg')} />
              <Chip label="🟠 Egg" active={foodType === 'egg'} onClick={() => setFoodType('egg')} />
            </>
          ) : (sides.products && sides.services) ? (
            <>
              <Chip label="📦 Products" active={itemType === 'product'} onClick={() => setItemType('product')} />
              <Chip label="🛠️ Services" active={itemType === 'service'} onClick={() => setItemType('service')} />
            </>
          ) : null}
          <Chip label={showAll ? '🏷️ All categories' : '🏷️ My categories'} active={showAll} onClick={() => setShowAll((v) => !v)} />
        </div>
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty emoji={words.emoji} title={`No ${words.many} found`} text={showAll ? `Admin has not added ${words.many} matching this yet.` : 'Nothing for your categories yet - try "All categories".'} />
      ) : [...sections, 'Other'].filter((sec) => items.some((d) => (d.menuCategory || 'Other') === sec)).map((sec) => (
        <div key={sec} style={{ marginBottom: 16 }}>
          <div className="h3" style={{ margin: '8px 0 10px' }}>{sec}</div>
          {items.filter((d) => (d.menuCategory || 'Other') === sec).map((d) => {
            const on = chosen.includes(d._id);
            return (
              <button
                type="button"
                key={d._id}
                className={`option-row ${on ? 'on' : ''}`}
                onClick={() => toggle(d._id)}
              >
                <span>{on ? '☑️' : '⬜️'}</span>
                {(d.images || [])[0] || d.image ? (
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={fileUrl((d.images || [])[0] || d.image)} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
                    {(d.images || []).length > 1 ? (
                      <span className="t-tiny" style={{ position: 'absolute', right: -4, bottom: -4, background: 'var(--ink)', color: '#fff', borderRadius: 8, padding: '0 5px' }}>
                        {d.images.length}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <span className="grow" style={{ minWidth: 0 }}>
                  <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                    {itemType === 'dish' ? <VegMark type={d.foodType} /> : null}
                    <span className="t-body">{d.title}</span>
                  </span>
                  <span className="t-tiny" style={{ display: 'block' }}>
                    {d.variants && d.variants.length
                      ? d.variants.map((v) => `${v.label} ${currency(v.price)}`).join(' · ')
                      : `${currency(d.price)}${itemType === 'service' ? ` ${d.priceUnit || ''}` : d.unit ? ` / ${d.unit}` : ''}`}
                    {d.brand ? ` · ${d.brand}` : ''}
                    {d.addonGroups && d.addonGroups.length ? ` · ${d.addonGroups.map((g) => g.title).join(', ')}` : ''}
                    {d.specs && d.specs.length ? ` · ${d.specs.map((s) => `${s.key} ${s.value}`).join(', ')}` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ))}

      <div style={{ position: 'sticky', bottom: 12, marginTop: 16 }}>
        <Button
          title={chosen.length ? `Add ${chosen.length} ${chosen.length > 1 ? words.many : words.one} to ${words.into}` : `Tick ${words.many} to add them`}
          disabled={!chosen.length}
          loading={adding}
          onClick={adopt}
          full
        />
      </div>
    </div>
  );
};

export default MasterMenuPage;
